// import api's and mutations
import { ClientAbsence } from 'RestAPIs/absence';

// import hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetClientAbsenceReasons,
  useGetClientAbsences
} from '../../queries';

// import components
import { Box, Button } from '@mui/material';
import { Dialog } from 'components/shared';
import { GridColDef } from '@mui/x-data-grid';
import AddAbsenceDialog from '../AddAbsenceDialog';
import DeleteAbsencePeriodDialog from './DeleteAbsencePeriodDialog';

// import types
import { ClientAbsencePeriodsDialogProps } from './ClientAbsencePeriodsDialog.types';

// import utils
import { formatByLocale } from 'utils/date';
import { dateSortComparator } from './utils/clientAbsencePeriodsDialog.utils';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { StyledTableBoxWrapper } from 'components/shared/Box/Box.styles';

export default function ClientAbsencePeriodsDialog({
  appointmentStart,
  clientId,
  clientName,
  handleClose,
  open
}: ClientAbsencePeriodsDialogProps) {
  const { t } = useTranslation('common');

  const [openAddAbsenceDialog, setOpenAddAbsenceDialog] = useState(false);
  const [openDeleteAbsenceDialog, setOpenDeleteAbsenceDialog] = useState(false);
  const [editData, setEditData] = useState<ClientAbsence | undefined>();

  const { data: absenceReasons } = useGetClientAbsenceReasons({
    enabled: open
  });
  const { data, isLoading } = useGetClientAbsences(
    { clientId: clientId as string },
    { enabled: !!clientId && open }
  );

  const columns: GridColDef[] = [
    {
      field: 'startDate',
      headerName: t('startDate'),
      sortComparator: dateSortComparator
    },
    {
      field: 'endDate',
      headerName: t('endDate'),
      sortComparator: dateSortComparator
    },
    { field: 'allAppointments', headerName: t('allAppointments') },
    {
      field: 'reasonId',
      headerName: t('reasonAbsence'),
      flex: 1
    },
    {
      field: 'description',
      headerName: t('description'),
      flex: 1
    }
  ];

  const rows =
    data?.map(
      ({
        absentAppointments,
        clientAbsenceId,
        reasonId,
        start,
        end,
        description
      }) => ({
        id: clientAbsenceId,
        reasonId:
          absenceReasons?.find(({ id }) => id === reasonId)?.name ??
          t('unknown'),
        allAppointments: absentAppointments ? t('no') : t('yes'),
        startDate: formatByLocale(new Date(start)),
        endDate: end == null ? '' : formatByLocale(new Date(end)),
        description
      })
    ) ?? [];

  function setAbsenceById(id) {
    const absence = data?.find(({ clientAbsenceId }) => clientAbsenceId === id);
    setEditData(absence);
  }

  function handleEditClick(id) {
    setAbsenceById(id);
    setOpenAddAbsenceDialog(true);
  }

  function handleDeleteClick(id) {
    setAbsenceById(id);
    setOpenDeleteAbsenceDialog(true);
  }

  return (
    <Dialog
      open={open}
      maxWidth="extralarge"
      handleClose={handleClose}
      onMouseOver={(e) => e.stopPropagation()}
      title={`Afwezigheden: ${clientName}`}
    >
      <Box sx={{ width: '768px' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setEditData(undefined);
            setOpenAddAbsenceDialog(true);
          }}
          sx={{ display: 'block', ml: 'auto' }}
        >
          {t('addTimes')}
        </Button>
        <StyledTableBoxWrapper>
          <TableCRUDLayout
            loading={isLoading}
            columns={columns}
            rows={rows}
            editable={false}
            onEditClick={handleEditClick}
            customActions={(id) => [
              {
                tooltipTitle: t('delete'),
                icon: 'trash-can',
                onClick: () => handleDeleteClick(id)
              }
            ]}
          />
        </StyledTableBoxWrapper>
      </Box>
      <AddAbsenceDialog
        appointmentStart={appointmentStart}
        clientId={clientId}
        clientName={clientName}
        editData={editData}
        handleClose={() => {
          setEditData(undefined);
          setOpenAddAbsenceDialog(false);
        }}
        open={openAddAbsenceDialog}
        title={t('activityPlanning.absenceDialogTitle', { clientName })}
      />
      <DeleteAbsencePeriodDialog
        clientAbsence={editData}
        clientId={clientId}
        handleClose={() => {
          setEditData(undefined);
          setOpenDeleteAbsenceDialog(false);
        }}
        open={openDeleteAbsenceDialog}
        title={`${t('activityPlanning.editAbsencePeriod')}: ${clientName}`}
      />
    </Dialog>
  );
}
